import Backbone from 'backbone'
import template from './values.hbs'
import $ from 'jquery'
import _ from 'lodash'
import calendars from 'scoreboard/components/calendars'
import ValuesForm from './values-form'
import i18n from 'scoreboard/components/i18n'
import globals from 'scoreboard/components/globals'
import appDisplay from 'scoreboard/components/app-display'
import formatScore from './format-score'
import config from 'scoreboard/components/config'
import constants from 'scoreboard/components/constants'
import useEquationValue from './use-equation-value.hbs'
import { shortenedAndUnshortened } from 'scoreboard/components/format-value'
import layoutManager from 'scoreboard/components/layout-manager'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import visible from 'scoreboard/scorecards/visible'
import DataType from 'scoreboard/datasets/model/data-type'
import EquationValueTooltips, { getSeriesKey } from '../equation-value-tooltips'

const { YES_NO } = DataType

/**
 * Return the Handlebars context that looks like this:
 *
 * <pre>
 * {
 *     series : [ {
 *         label : 'Actual',
 *         updateType : 'manual'
 *     }, {
 *         label : 'Red Flag',
 *         updateType : 'manual'
 *     }, {
 *         label : 'Goal',
 *         updateType : 'manual'
 *     } ],
 *     scores: [ {
 *         actual: '2',
 *         applicable: true,
 *         color: 'red',
 *         periodId: 1013,
 *         periodLabel: 'December 2014',
 *         score: 3,
 *         thresholds: [ 4, 3 ]
 *     } ]
 * }
 * </pre>
 */
function context(model) {
	var formatParams = {
		dataType: model.get('dataType'),
		currencyCode: model.get('currencyCode'),
		precision: model.get('precision'),
		scoringTypeId: model.get('scoringTypeId')
	}

	var scores = _.map(model.get('actualAndThresholdValuePeriodIds'), function(id) {
		var score = model.score(id)
		if (!score) {
			score = { periodId: id, applicable: true }
			var scoringType = config.scoringType(model.get('scoringTypeId'))
			if (scoringType.thresholdNames) {
				score.thresholds = []
				_.each(scoringType.thresholdNames, function() {
					score.thresholds.push(null)
				})
			}
		}
		let scoreFormatParams = _.defaults({ dataType: score.dataType }, formatParams)
		return formatScore(score, scoreFormatParams)
	})

	let scoringType = config.scoringType(model.get('scoringTypeId'))
	let scored = scoringType.kpiValueType != constants.UNSCORED_TEXT &&
		_.get(scoringType, 'standardScoringType') != constants.UNSCORED

	return {
		series: model.get('series'),
		scores,
		scoringType,
		scored
	}

}

const na = () => i18n.get('scorecards.node.metric.notapplicable')
// #51712 the DEFAULT_APPLICABLE_STATUS in the app is true, so
// only consider a score not applicable (N/A) if score.applicable is
// specifically set to false
const isNA = (score) => score?.applicable === false

/**
 * The Actual and Thresholds Value panel on the Scorecards Overview
 */
var ActualAndThresholdValues = Backbone.View.extend({

	// this.model is a ScorecardNode

	className: 'panel panel-with-table',

	events: {
		'click .edit-item': 'edit',
		'mouseenter .has-equation': '_hoverEquationValue',
		'mouseleave .has-equation': '_hoverEquationValue'
	},

	render: function() {
		// handle when calendars need to be extended
		var periodIds = this.model.get('actualAndThresholdValuePeriodIds')
		if (!periodIds || periodIds.length == 0) {
			var calendarLabel = calendars.calendarsById[this.model.get('calendarId')].label
			var period = calendars.periodsById[globals.periodId].label
			var msg = i18n.get('scorecards.overview.metric.noData', calendarLabel, period)
			this.$el.empty().append(template({ msg: msg }))
			this._calendar = null // force a full render next time render is called
			return this
		}

		var ctx = context(this.model)
		var calendar = calendars.periodsById[globals.periodId].calendar

		// if we just need to change colors we're done
		if (this._calendar === calendar &&
				// and there are the same number of scores as there are rows
				// (the -1 accounts for the header row).  fixes #9556
				ctx.scores.length == this.$('tr').length - 1) {
			this.rerender(ctx)
			return this
		}

		// if we reach here, we have a data table to create or update
		this.$el.empty().append(template(ctx))
		// add the .has-equation class as appropriate as well as related equation
		// and column header data
		_.each(this.model.get('series'), function(series, index) {
			let isReplicatedDatasetMetric = this.model.get('replicated') && series.updateType === 'dataset'
			if (!_.isEmpty(series.equation)) {
				let element = index == 0 ? this.$('.actual .value') : this.$('.threshold' + (index - 1) + ' .value')
				element.addClass('has-equation text-link-emphasized')
					.attr('data-scorecard-node-id', this.model.id)
					.attr('data-unshortened-equation', series.equation)
					.attr('data-unshortened-equation-header', i18n.get('scorecards.node.metric.equation.withArg', this.getSeriesKey(index)))
					.attr('data-unshortened-header', i18n.get(this.getSeriesKey(index)) + ': ' + calendars.periodsById[globals.periodId].label)
				element.toggleClass('replicated-dataset-metric', isReplicatedDatasetMetric)
			}
		}.bind(this))
		this.renderUseEquationValues()
		// now populate the table
		this.rerender(ctx)

		// after emptying view need to re-delegate events
		this.delegateEvents(this.events)

		// hide the edit button if there are no series with manual update type
		let editableSeries = !_.some(ctx.series, s => _.isEmpty(s.equation))
		if (editableSeries) {
			this.$('.edit-item').hide()
		}

		if (!config.scoresVisible) {
			this.$('.score').hide()
		}

		this.secure()
		// record the calendar currently rendered.  if it changes we'll need to
		// redraw the table
		this._calendar = calendar

		this.setupEquationValueTooltips()

		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })

		return this
	},

	_hoverEquationValue: function(e) {
		this.$(e.target).toggleClass('is-highlighted')
	},

	/**
	 * Render the "Use Equation" values, which appears above the data table
	 * in the "Actual and Threshold Values" panel on the Scorecards Overview
	 */
	renderUseEquationValues: function() {
		let hasRollupSeries = _.some(this.model.get('series'), series =>
			series.updateType === 'rollup')
		if (!this.model.get('useEquationForAggregations') && !hasRollupSeries) {
			return
		}

		// hide columns that don't make sense with use equation, if appropriate

		var allEquations = true // whether all the series have equations
		// the first 3 columns are label, score and actual, so the threshold
		// indexes start at 0. Note that this index doesn't vary depending on
		// whether scores are visible since the score column is always present
		// in the HTML; it's just hidden with JS (#50682)
		var baseIndex = 3
		_.each(this.model.get('series'), function(series, index) {
			if (this.isBypassingEquation(series)) {
				allEquations = false
			} else {
				// hide the column in the "Actual and Threshold Values" for this series
				var idx = baseIndex + index
				var selector =
					'tr td:nth-child(' + idx + '), ' +
					'tr th:nth-child(' + idx + ')'
				this.$(selector).addClass('is-not-displayed')

				// write something above the data table like
				// "Actual Value 2016 = 312" (the period and the value 312 are
				// filled by the rerender method)

				this.$('.use-equation-values')
					.append(useEquationValue({
						series: i18n.get(this.getSeriesKey(index)),
						type: index == 0 ? 'actual' : 'threshold',
						index: index == 0 ? '' : index - 1
					}))
					.removeClass('is-not-displayed')
			}
		}.bind(this))
		// if there's only the list of periods visible, hide the whole table.
		// There may have been scores displayed, but those were for the months
		// and those values don't really factor into the yearly score, so it's
		// misleading to show them
		if (allEquations) {
			this.$('table').addClass('is-not-displayed')
		}
	},

	/**
	 * Indicates whether the equation is being bypassed in favor of aggregated
	 * the values from native periods into the larger period. Logic here should
	 * be mirrored in CalculatedSeriesValueCalculator:isBypassingEquation
	 */
	isBypassingEquation: function(series) {
		// logic below here is specific to the world of JavaScript

		let rollupOrUseEquation = series.updateType === 'rollup' ||
			this.model.get('useEquationForAggregations')
		if (_.isEmpty(series.equation) || !rollupOrUseEquation) {
			return true
		}

		// logic below here should mirror
		// CalculatedSeriesValueCalculator:isBypassingEquation

		let aggregatedPeriod = calendars.isAggregatedPeriod(
			this.model.get('calendarId'), globals.periodId)
		let doNotUseEquation = false == this.model.get('useEquationForAggregations')
		let calculatedYesNo = this.model.get('updateType') === 'calculated' &&
			this.model.get('updateType') == YES_NO
		return aggregatedPeriod && (doNotUseEquation || calculatedYesNo)
	},

	// change the values in the panel rather than redrawing the whole thing.
	// this is needed for css transitions to work
	rerender: function(ctx) {
		var rows = _.tail(this.$('tr')) // first row is the headers

		const scoreText = (score,value) => {
			const {shortened, unshortened} = value
			return isNA(score) ? na() : (layoutManager.export ? unshortened : shortened)
		}

		_.each(ctx.scores, (score, i) => {
			var row = $(rows[i])
			row.data('period-id', score.periodId)
			row.find('.label').attr('class', 'label ' + score.color)
			row.find('.label-text').text(score.periodLabel)
			row.find('.score')
				.attr('class', 'score ' + score.color)
				.text(isNA(score) ? na() : score.score)
			var actualEl = row.find('.actual .value')
			var actual = scoreText(score, score.actual)
			actualEl.toggleClass('is-empty', _.isEmpty(actual))
				.removeAttr('data-unshortened-value data-unshortened-header')
				.text(actual)

			// If there's an equation or if the shortened actual value != the unshortened actual value,
			// append the data-unshortened-value attribute
			const {shortened, unshortened} = score.actual
			if (actualEl.hasClass('has-equation') || shortened != unshortened) {
				actualEl.attr('data-unshortened-value', unshortened)
					.attr('data-scorecard-node-id', this.model.id)
					.attr('data-unshortened-header', i18n.get(this.getSeriesKey(0)) + ': ' + score.periodLabel)
			}

			_.each(score.thresholds, (threshold, j) => {
				var thresholdText = scoreText(score, threshold)
				,	el = row.find('.threshold' + j + ' .value')

				el.toggleClass('is-empty', _.isEmpty(thresholdText))
					.removeAttr('data-unshortened-value data-unshortened-header')
					.text(thresholdText)

				// If there's an unshortened value != to the shortened version,
				// or if there's an equation to be shown for this value,
				// append the data-unshortened-value attribute
				if (threshold.shortened != threshold.unshortened || el.hasClass('has-equation')) {
					el.attr('data-unshortened-value', threshold.unshortened)
						.attr('data-scorecard-node-id', this.model.id)
						.attr('data-unshortened-header', i18n.get(this.getSeriesKey(j + 1)) + ': ' + score.periodLabel)
				}
			})

			// Apply unshortened header data for non-equation values
			_.each(this.model.get('series'), (series, index) => {
				if (_.isEmpty(series.equation)) {
					if (index == 0) {
						row.find('.actual .value').attr('data-unshortened-header',  series.label + ': ' + score.periodLabel)
					} else {
						row.find('.threshold' + (index - 1) + ' .value').attr('data-unshortened-header', series.label + ': ' + score.periodLabel)
					}
				}
			})
		})

		this.rerenderUseEquationValues()

		this.setupEquationValueTooltips()

		this.toggleEditForVisibility()

		return this
	},

	rerenderUseEquationValues: function() {
		// re-render the "use equation" values above the table
		var useEquationIndex = 0
		_.each(this.model.get('series'), function(series, index) {
			// #36385 adjust to match #36083 so we are on the right series
			let rollupOrUseEquation = series.updateType === 'rollup' ||
				this.model.get('useEquationForAggregations')
			if (_.isEmpty(series.equation) || !rollupOrUseEquation) {
				return
			}

			var score = this.model.score(globals.periodId)
			var value = ''
			// if there's no score object, the value for the period was blank
			if (score) {
				value = index == 0 ? score.actual : score.thresholds[index - 1] && score.thresholds[index - 1]
				// #37744 handle N/A properly
				value = isNA(score) ? na() : value
			}

			var {shortened, unshortened} = shortenedAndUnshortened({
				dataType: _.get(score, 'dataType') || this.model.get('dataType'),
				currencyCode: this.model.get('currencyCode'),
				precision: this.model.get('precision'),
				value: value
			})
			value = layoutManager.export ? unshortened : shortened
			var el = this.$('.use-equation-value:eq(' + (useEquationIndex++) + ')')
			let scoreInPeriodId = this.model.get('scoreInPeriodId')
			let periodLabel = calendars.periodsById[scoreInPeriodId].label
			el.find('.period').text(periodLabel)
			el.find('.value')
				.toggleClass('is-empty', _.isEmpty(value))
				.html(value)

			el.find('.value')
				.attr('data-scorecard-node-id', this.model.id)
				.attr('data-unshortened-equation', series.equation)
				.attr('data-unshortened-equation-header', i18n.get('scorecards.node.metric.equation.withArg', this.getSeriesKey(index)))
				.attr('data-unshortened-value', unshortened)
				.attr('data-unshortened-header', i18n.get('scorecards.node.metric.equation.withArg', this.getSeriesKey(index)))
				.addClass('text-link-emphasized')
		}.bind(this))
	},

	edit: function() {
		appDisplay.dialog(new ValuesForm({ model: this.model }))
	},

	secure: function() {
		let hideUpdateButton = this.model.get('pendingDeletion') ||
			(!this.model.get('updateActual') && !this.model.get('updateThresholds'))
		if (hideUpdateButton) {
			this.$('.edit-item').hide()
			// #29125 also hide the import values icon
			this.$('.import').hide()
		}
	},

	/**
	 * Set things up so a tooltip displays when you hover over a value in the
	 * data table.
	 */
	setupEquationValueTooltips: function() {
		if (!this.equationValueTooltips) {
			this.equationValueTooltips = new EquationValueTooltips({
				el: this.el,
				metrics: [ this.model ]
			})

		}
		
		this.equationValueTooltips.render()
	},

	/**
	 * Given an index within this.model.get('series'), returns the resource
	 * bundle key for the series, e.g. 'scorecards.node.metric.actualValue'
	 */
	getSeriesKey: function(index) {
		return getSeriesKey(this.model.get('scoringTypeId'), index)
	},

	/**
	 * toggles display of update button based on item's visibility
	 * due to start and archive dates
	 */
	toggleEditForVisibility: function() {
		let period = calendars.periodsById[globals.periodId]
		let isVisible = visible({
			start: this.model.get('ancestorStart'),
			archive: this.model.get('ancestorArchive')
		}, period)
		this.$('.edit-item').toggleClass('is-not-displayed', !isVisible)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.equationValueTooltips, this.covertButtonTooltips], 'remove')
	}

})

export default ActualAndThresholdValues
export { context }
export { isNA }

ActualAndThresholdValues.__filename__ = 'scorecards/overview/values.js'
